
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  action?: { label: string; onClick: () => void };
}

type AddToastFn = (message: string, type?: ToastType, action?: { label: string; onClick: () => void }) => void;

const ToastContext = createContext<AddToastFn>(() => {});

export const useToast = (): AddToastFn => useContext(ToastContext);

const DURATION: Record<ToastType, number> = {
  success: 3000,
  info: 3500,
  warning: 4500,
  error: 5000,
};

const CONFIG: Record<ToastType, { icon: React.ComponentType<{ size?: number; className?: string }>; borderColor: string; iconColor: string }> = {
  success: { icon: CheckCircle,    borderColor: 'border-l-emerald-500', iconColor: 'text-emerald-400' },
  error:   { icon: XCircle,        borderColor: 'border-l-red-500',     iconColor: 'text-red-400'     },
  info:    { icon: Info,            borderColor: 'border-l-sky-500',     iconColor: 'text-sky-400'     },
  warning: { icon: AlertTriangle,  borderColor: 'border-l-amber-500',   iconColor: 'text-amber-400'   },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    // Don't auto-dismiss toasts with action buttons — user must tap or close
    let exitTimer: number | undefined;
    if (!toast.action) {
      exitTimer = window.setTimeout(() => {
        setVisible(false);
        window.setTimeout(onDismiss, 300);
      }, DURATION[toast.type]);
    }
    return () => {
      cancelAnimationFrame(frame);
      if (exitTimer !== undefined) window.clearTimeout(exitTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { icon: Icon, borderColor, iconColor } = CONFIG[toast.type];

  return (
    <div
      className={`flex items-start gap-3 pl-3 pr-2 py-3 bg-[var(--bg-surface)] border border-[var(--border-secondary)] border-l-4 ${borderColor} rounded-xl shadow-2xl w-80 max-w-[calc(100vw-2rem)] transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
    >
      <Icon size={17} className={`${iconColor} mt-0.5 flex-shrink-0`} />
      <span className="text-sm text-[var(--text-primary)] flex-1 leading-snug">{toast.message}</span>
      {toast.action && (
        <button
          onClick={() => { toast.action!.onClick(); setVisible(false); window.setTimeout(onDismiss, 300); }}
          className={`text-xs font-semibold ${iconColor} flex-shrink-0 whitespace-nowrap underline underline-offset-2`}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => { setVisible(false); window.setTimeout(onDismiss, 300); }}
        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] flex-shrink-0 p-0.5 ml-1 transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-6 z-[9999] flex flex-col gap-2 items-center md:items-end pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onDismiss={() => onDismiss(t.id)} />
        </div>
      ))}
    </div>
  );
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback<AddToastFn>((message, type = 'success', action) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts(prev => [...prev.slice(-4), { id, type, message, action }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};
